'use client';

import {DotSeparator} from 'common/components/dot_separator';
import type {ExperienceGateVariant} from 'common/experiments';
import {formatDateToQuarter} from 'common/utils';
import {FRONTEND_URL} from 'config';
import {AuthForm} from 'iq-auth/components/auth_form';
import {useSignupCta} from 'iq-auth/hooks';
import {OutcomeBadge} from 'iq-interview_experiences/components/outcome_badge';
import type {InterviewExperience} from 'iq-interview_experiences/types';
import {
  trackGuideExperienceSignupClicked,
  trackGuideExperienceSubscribeClicked,
} from 'iq-interview_guides/tracking';
import {usePremiumPrices} from 'iq-upselling/hooks';
import {Lock} from 'lucide-react';
import {usePathname} from 'next/navigation';
import {useState} from 'react';

// Rows beyond this collapse into a "+ N more reports" line so the card stays short.
const MAX_ROWS = 5;

const PRIMARY_CTA_CLASS =
  'w-full py-3 bg-iq-red text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity';

export function usePricingHref() {
  const pathname = usePathname();
  return `${FRONTEND_URL}/pricing?next=${encodeURIComponent(pathname)}`;
}

/**
 * Locked reports as rows of metadata that is already public on the guide (role,
 * interview quarter, outcome). Never renders report text or previews.
 */
export function LockedReportRows({
  experiences,
  positionName,
}: {
  experiences: InterviewExperience[];
  positionName: string;
}) {
  const hiddenCount = experiences.length - MAX_ROWS;

  return (
    <div className='flex flex-col gap-2 text-left'>
      {experiences.slice(0, MAX_ROWS).map((experience) => (
        <div
          key={experience.id}
          className='flex items-center gap-3 bg-white border border-iq-grey-light rounded-lg px-4 py-3'>
          <div className='text-sm text-iq-grey-dark flex-1 min-w-0'>
            <strong>{experience.position_display_name || positionName}</strong>
            <DotSeparator className='mx-2' />
            <span>Interviewed {formatDateToQuarter(experience.interview_date)}</span>
          </div>
          {experience.outcome !== null && (
            <div className='max-w-40 flex justify-end shrink-0'>
              <OutcomeBadge outcome={experience.outcome} outcome_color={experience.outcome_color} />
            </div>
          )}
          <Lock size={14} className='shrink-0 text-iq-grey-medium' />
        </div>
      ))}
      {hiddenCount > 0 && (
        <p className='text-xs text-iq-grey-medium text-center'>
          + {hiddenCount} more {hiddenCount === 1 ? 'report' : 'reports'}
        </p>
      )}
    </div>
  );
}

/** "Unlock all N with Premium: $X/yr or $Y/mo", yearly first, linking to /pricing. */
export function PremiumPriceLine({totalCount, onClick}: {totalCount: number; onClick: () => void}) {
  const prices = usePremiumPrices();
  const pricingHref = usePricingHref();

  return (
    <a
      href={pricingHref}
      onClick={onClick}
      className='block text-sm text-iq-grey-medium underline hover:text-iq-black transition-colors'>
      Unlock all {totalCount} with Premium
      {prices && `: $${prices.yearly}/yr or $${prices.monthly}/mo`}
    </a>
  );
}

interface ExperienceGateCardProps {
  companyName: string;
  positionName: string;
  experiences: InterviewExperience[];
  guideSlug: string;
  variant: ExperienceGateVariant;
}

/**
 * guide_anon_experience_gate_v1, experiment arm, anonymous visitors: stands in for the
 * whole report list. The page has already stripped every report's text on the server;
 * signing up opens the same inline AuthForm the locked-guide gate uses, and after the
 * reload the normal free tier applies (first report readable).
 */
export default function ExperienceGateCard({
  companyName,
  positionName,
  experiences,
  guideSlug,
  variant,
}: ExperienceGateCardProps) {
  const [signupOpen, setSignupOpen] = useState(false);
  const startSignup = useSignupCta('experience_gate');
  const totalCount = experiences.length;

  function openSignup() {
    void trackGuideExperienceSignupClicked({slug: guideSlug, variant});
    startSignup(() => setSignupOpen(true));
  }

  if (signupOpen) {
    return (
      <div className='bg-gray-50 border border-iq-grey-light rounded-lg p-8 flex justify-center'>
        <div className='bg-white rounded-2xl shadow-sm w-full max-w-sm p-8'>
          {/* AuthForm opens on its signup tab by default and offers its own switch to login. */}
          <AuthForm onSuccess={() => window.location.reload()} onBack={() => setSignupOpen(false)} />
        </div>
      </div>
    );
  }

  return (
    <div className='bg-gray-50 border border-iq-grey-light rounded-lg p-8 text-center'>
      <h3 className='text-lg font-bold text-iq-black mb-4'>
        {totalCount} interview {totalCount === 1 ? 'report' : 'reports'} from {companyName}{' '}
        {positionName} candidates
      </h3>
      <LockedReportRows experiences={experiences} positionName={positionName} />
      <div className='mt-6 flex flex-col items-center gap-3 max-w-sm mx-auto'>
        <button onClick={openSignup} className={PRIMARY_CTA_CLASS}>
          Sign up free to read a report
        </button>
        <PremiumPriceLine
          totalCount={totalCount}
          onClick={() =>
            void trackGuideExperienceSubscribeClicked({slug: guideSlug, variant, gateMode: 'public'})
          }
        />
      </div>
    </div>
  );
}
